import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import {useGetSummaryView} from '../../../hooks/productions/useProductions';
import {GetSummaryViewRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const SummaryViewApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [moduletype, setModuletype] = useState<string>('PRODUCTION');
  const [productionId, setProductionId] = useState<number>(216);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetSummaryViewRequestParam = {
    SecurityGroupId: securityGroupId,
    moduletype: moduletype,
    productionId: productionId,
  };

  const {
    data: summaryData,
    isLoading,
    error,
    refetch,
  } = useGetSummaryView(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (summaryData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(summaryData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Summary View API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get production summary view with document counts, page counts, status
          information, and production statistics.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip
              label="GET"
              color="primary"
              size="small"
              data-testid={`summary-view-chip-info`}
            />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Summary View
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/view/summaryview
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get production summary view with comprehensive statistics including
            document counts, page counts, status information, and production
            metrics.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduletype}
              onChange={(e) => setModuletype(e.target.value)}
              size="small"
              placeholder="e.g., PRODUCTION, EXPORT"
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="summary-view-api-test-test-api-button"
            >
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="summary-view-api-test-reset-button"
            >
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="summary-view-api-test-refetch-button"
              >
                ♻️ Refetch
              </Button>
            )}
            {summaryData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="summary-view-api-test-button"
              >
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Fetching summary view...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while fetching summary view'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {summaryData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Summary View Data
              </Typography>

              {/* Basic Information */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Basic Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Module Type:</strong> {summaryData.moduleType}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production ID:</strong>{' '}
                        {summaryData.productionID}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Security Group ID:</strong>{' '}
                        {summaryData.securityGroupId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is Mark Complete:</strong>{' '}
                        {summaryData.isMarkComplete ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Status:</strong>{' '}
                        <Chip
                          label={summaryData.productionStatusCode}
                          color={
                            summaryData.productionStatusCode === 'FAILED'
                              ? 'error'
                              : summaryData.productionStatusCode === 'COMPLETE'
                                ? 'success'
                                : 'default'
                          }
                          size="small"
                          data-testid={`summary-view-chip-error-success`}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Encrypted Production ID:</strong>{' '}
                        {summaryData.encyrptProductionId}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Document Statistics */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Document Statistics
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Total Documents:</strong>{' '}
                        {summaryData.totalDocuments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Total Pages:</strong> {summaryData.totalPages}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Number of Custodians:</strong>{' '}
                        {summaryData.numberOfCustodians}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Number of Natives:</strong>{' '}
                        {summaryData.numberOfNatives}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Exception Files:</strong>{' '}
                        {summaryData.numberOfExceptionFiles}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Missing Text Files:</strong>{' '}
                        {summaryData.missingTextFiles}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>First Doc ID:</strong> {summaryData.firstDocID}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Last Doc ID:</strong> {summaryData.lastDocID}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Production Processing Statistics */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Production Processing Statistics
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Privileged Documents:</strong>{' '}
                        {summaryData.privilegedDocuments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Documents by Production Guard:</strong>{' '}
                        {summaryData.documentsIdentifiedByProductionGuard}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Redacted PDF/TIFF Documents:</strong>{' '}
                        {summaryData.redactedDocuments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Redacted Excel Documents:</strong>{' '}
                        {summaryData.redactedDocuments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Redacted Native File Variants:</strong>{' '}
                        {summaryData.redactedNativeFileVariants}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Redacted Audio Documents:</strong>{' '}
                        {summaryData.redactedDocuments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Documents to OCR:</strong>{' '}
                        {summaryData.documentsToOCR}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Pages to OCR:</strong> {summaryData.pagesToOCR}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Documents to TIFF:</strong>{' '}
                        {summaryData.documentsToTiff}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Pages to TIFF:</strong>{' '}
                        {summaryData.pagesToTiff}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Documents Printed in Color:</strong>{' '}
                        {summaryData.documentsPrintedInColor}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Pages Printed in Color:</strong>{' '}
                        {summaryData.pagesPrintedInColor}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>MP3 File Count:</strong>{' '}
                        {summaryData.mP3FileCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Has TIFF/PDF:</strong>{' '}
                        {summaryData.hasTiffPDF ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Summary Page Calculation Status:</strong>{' '}
                        {summaryData.summaryPageCalculationStatus}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(summaryData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default SummaryViewApiTest;
